"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Camera, Clock3, Search } from "lucide-react";
import styles from "./mobile-product-search.module.css";
import { productSearchDestination } from "@/lib/product-link";
import { readLocalePreferences } from "@/lib/locale-preferences";

type MobileProductSearchProps = {
  open: boolean;
  onClose: () => void;
  initialMode?: "product" | "image";
  placeholder?: string;
};

export function MobileProductSearch({
  open,
  onClose,
  initialMode = "product",
  placeholder = "Enter the link or keyword",
}: MobileProductSearchProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 80);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const submit = () => {
    const value = query.trim();
    if (!value) return;
    window.location.assign(productSearchDestination(value, readLocalePreferences().language));
  };

  return (
    <div
      ref={dialogRef}
      className={styles.dialog}
      role="dialog"
      aria-modal="true"
      aria-label="Product link search"
    >
      <header className={styles.header}>
        <button className={styles.iconButton} type="button" aria-label="Close search" onClick={onClose}>
          <ArrowLeft size={21} aria-hidden="true" />
        </button>
        <form
          className={styles.form}
          action="/product/search"
          method="get"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Search size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            name="q"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            aria-label={placeholder}
          />
          <button className={styles.submit} type="submit">Search</button>
        </form>
      </header>

      <div className={styles.content}>
        <section aria-label="Search modes">
          <div className={styles.modeGrid}>
            <button
              className={initialMode === "product" ? styles.modeActive : styles.mode}
              type="button"
              aria-current={initialMode === "product" ? "true" : undefined}
            >
              <Search size={17} aria-hidden="true" />
              Product search
            </button>
            <button
              className={styles.mode}
              type="button"
              aria-disabled="true"
              disabled
              title="Image Search · Coming Soon"
            >
              <Camera size={17} aria-hidden="true" />
              Image Search
              <span>Coming Soon</span>
            </button>
          </div>
        </section>

        <section className={styles.history} aria-label="Search history">
          <div className={styles.sectionHead}>
            <strong>Search History</strong>
          </div>
          <div className={styles.honestEmpty}>
            <Clock3 size={18} aria-hidden="true" />
            <span>Saved search history is coming soon.</span>
          </div>
        </section>

        <section>
          <div className={styles.sectionHead}>
            <strong>Quick access</strong>
          </div>
          <div className={styles.chips}>
            <Link href="/shipping-estimate" onClick={onClose}>Shipping Estimate</Link>
            <Link href="/diy-order" onClick={onClose}>DIY Order</Link>
            <Link href="/help-center" onClick={onClose}>Help Center</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
